import { Component, computed, DestroyRef, effect, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { PrintersModuleService } from '../../services/printers-module.service';
import { EntityManagerService, LoaderService } from 'src/app/core';
import { CommonModule } from '@angular/common';
import { MainBaseComponent } from 'src/app/shared/components/master-detail-layout/main-base/main-base.component';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrinterFormService } from '../../services/printer-form.service';
import { mobileCheck } from 'src/utilities';
import { PrinterConfigurationComponent } from './printer-configuration/printer-configuration.component';
import { PrinterOperationsComponent } from './printer-operations/printer-operations.component';
import { PrinterSettingsComponent } from './printer-settings/printer-settings.component';
import { PrinterStubComponent } from './printer-stub/printer-stub.component';
import { Printers } from 'tilby-models';
import { PrinterNonFiscalConfigurationComponent } from './printer-non-fiscal-configuration/printer-non-fiscal-configuration.component';
import { AlertDialogService, ConfirmDialogService, PrinterErrorFiscalDialogService } from 'src/app/dialogs';
import { $state, FiscalPrinters } from 'app/ajs-upgraded-providers';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PrintersFormGroups } from '../../models/settings-printers.model';
import { PrinterNavigationStateService } from '../../services/printer-navigation-state.service';
import { LocalLoaderService } from 'src/app/shared/services/local-loader.service';
import { TruncatedTextDirective } from 'src/app/directives/truncated-text.directive';

export interface DetailTab {
  id: string;
  label: string;
  types: string[];
}

const ALL_TABS: DetailTab[] = [
  { id: 'operations', label: 'PRINTERS.DETAIL.OPERATIONS', types: ['fiscal', 'rt'] },
  // { id: 'categories', label: 'PRINTERS.DETAIL.CATEGORIES', types: ['nonfiscal'] },
  // { id: 'items', label: 'PRINTERS.DETAIL.ITEMS', types: ['nonfiscal'] },
  // { id: 'rooms', label: 'PRINTERS.DETAIL.ROOMS', types: ['nonfiscal'] },
  // { id: 'cashregisters', label: 'PRINTERS.DETAIL.CASHREGISTERS', types: ['nonfiscal'] },
  // { id: 'otherPrinters', label: 'PRINTERS.DETAIL.OTHER_PRINTERS', types: ['nonfiscal'] },
  { id: 'non_fiscal_configuration', label: 'PRINTERS.DETAIL.CONFIGURATION', types: ['nonfiscal'] },
  { id: 'settings', label: 'PRINTERS.DETAIL.SETTINGS', types: ['fiscal', 'rt', 'nonfiscal', 'receipt', 'kds'] },
  // { id: 'documents', label: 'PRINTERS.DETAIL.DOCUMENTS', types: ['fiscal', 'rt', 'receipt'] },
  { id: 'configuration', label: 'PRINTERS.DETAIL.SUMMARY', types: ['fiscal', 'rt'] },
  { id: 'stub', label: 'PRINTERS.DETAIL.STUB', types: ['fiscal', 'rt', 'receipt'] },
];
@Component({
  selector: 'app-printer-details',
  standalone: true,
  imports: [
    CommonModule,
    MainBaseComponent,
    ActionBtnComponent,
    PrinterConfigurationComponent,
    PrinterOperationsComponent,
    PrinterSettingsComponent,
    PrinterStubComponent,
    PrinterNonFiscalConfigurationComponent,
    MatTabsModule, MatButtonModule,
    TruncatedTextDirective,
    TranslateModule
  ],
  templateUrl: './printer-details.component.html',
  styleUrl: './printer-details.component.scss'
})
export class PrinterDetailsComponent {

  private readonly printersModuleService = inject(PrintersModuleService);
  private readonly loaderService = inject(LoaderService);
  private readonly localLoader = inject(LocalLoaderService);
  protected readonly printerFormService = inject(PrinterFormService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly entityManagerService = inject(EntityManagerService);
  private readonly state = inject($state);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly printerErrorFiscal = inject(PrinterErrorFiscalDialogService);
  private readonly fiscalPrintersService = inject(FiscalPrinters);
  private readonly printerNavigationStateService = inject(PrinterNavigationStateService);

  @ViewChild('tabContent') tabContent!: ElementRef;
  
  protected printerForm!: PrintersFormGroups;

  protected tabChange: number = 0;
  protected activeTab = signal<string>('operations');

  protected isMobile = mobileCheck();
  // the printer this component is showing; cleared on destroy so late responses are ignored
  private lastLoadedId: string | null = null;
  private groupedPrinters: Map<string, Printers[]> = new Map();

  protected readonly actions: Record<string, boolean>;
  protected printerData = signal<any>(null);
  protected titlePage = '';

  protected visibleTabs = computed(() => {
    const type = this.printerData()?.printer?.type;
    return ALL_TABS.filter(tab => tab.types.includes(type));
  });
  

  constructor() {
    inject(DestroyRef).onDestroy(() => this.lastLoadedId = null);

    effect(() => {
      if(this.printersModuleService.printersMapLoaded()) {
        this.groupedPrinters = this.printersModuleService.printersMap;
      }
    })

    // select the first tab of the printer: deferred, as the active tab is bound in the template
    effect(() => {
      const tabs = this.visibleTabs();

      setTimeout(() => {
        const initialActiveTab = tabs[0]?.id || 'settings';
        this.activeTab.set(initialActiveTab);
        this.setActions(initialActiveTab);
      });
    });

    effect(() => {
      const current = this.printersModuleService.selection();

      if (current?.type === 'PRINTER' && current.printer?.id) {
        this.loadData(current?.printer?.id?.toString());
      }
    }, { allowSignalWrites: true });

    this.actions = {
      showDelete: true,
      showSave: false,
      showConfigure: false
    }
  }

  async loadData(id: string) {
    if (this.lastLoadedId == id) return;

    this.lastLoadedId = id;
    this.localLoader.show();
    try {
      await this.printersModuleService.loadGlobalData();
      const detail = await this.printersModuleService.getPrinterDetail(id);

      // another printer was selected (or the component destroyed) while loading
      if (this.lastLoadedId !== id) return;
      if (!detail?.printer) throw new Error(`Printer ${id} not found`);

      this.printersModuleService.getGeneralPreferences();
      // build the form first: if it throws, the shared selection has not been touched yet
      this.printerForm = this.printerFormService.createForm(detail.printer as Printers);
      this.printersModuleService.updateSelection("PRINTER", detail);
      this.printersModuleService.getAvailableDocumentTypes(detail.printer.type as any);
      this.printerData.set(detail);

      const indices = this.findPrinterIndices(parseInt(id));
      if(indices) this.scrollSidebar(indices.groupIndex, indices.itemIndex);

    } catch (error) {
      // keep lastLoadedId so the selection effect does not retry in a loop
      if (this.lastLoadedId === id) {
        this.alertDialogService.openDialog({
          data: {
            messageLabel: this.translateService.instant('PRINTERS.DETAIL.LOAD_ERROR')
          }
        });
      }
    } finally {
      this.localLoader.hide();
    }
  }

  findPrinterIndices(targetPrinterId: number): { groupIndex: number, itemIndex: number } | null {
    const groups = Array.from(this.groupedPrinters.entries());

    for (let i = 0; i < groups.length; i++) {
      const [_, printers] = groups[i];
      const j = printers.findIndex(p => p.id === targetPrinterId);
      if (j !== -1) {
        return { groupIndex: i, itemIndex: j };
      }
    }

    return null;
  }

  scrollSidebar(groupIndex: number, itemIndex: number) {
    const element = document.getElementById(`printer-item-content-${groupIndex}-${itemIndex}`);
    const container = document.getElementById('sidebarContent');

    if (element && container) {
      const containerTop = container.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      
      const offset = 100;
      const scrollPosition = (elementTop - containerTop) + container.scrollTop - offset;

      container.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  }

  changeTab(newTabId: string) {
    const oldTabId = this.activeTab();
    if (oldTabId === newTabId) return;

    this.setActions(newTabId);
    this.activeTab.set(newTabId);

    this.tabContent?.nativeElement.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }

  setActions(tab: string) {
    switch(tab) {
      case 'operations':
        this.actions['showSave'] = false;
        this.actions['showConfigure'] = false;
        break;
      case 'non_fiscal_configuration':
        this.actions['showSave'] = true;
        this.actions['showConfigure'] = false;
        break;
      case 'settings':
        this.actions['showSave'] = true;
        this.actions['showConfigure'] = false;
        break;
      case 'configuration':
        this.actions['showSave'] = false;
        this.actions['showConfigure'] = true;
        break;
      case 'stub':
        this.actions['showSave'] = true;
        this.actions['showConfigure'] = false;
        break;
    }
  }

  async save() {
    const proceed = await this.confirmDialogService.openDialog({
      data: { 
        messageLabel: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_SAVE')
      }
    });
    if (!proceed) return;

    this.loaderService.show();
    try {
      const printerToSave = this.printerFormService.preparePrinterForSave();

      const printer = await this.entityManagerService.printers.putOneOnline(printerToSave as Printers);
      this.printersModuleService.updatePrinterSelected(printer);
      this.printerNavigationStateService.initialData = structuredClone(this.printerFormService.form?.getRawValue());
      // this.state.reload('app.new.settings.printers.detail', { id: this.printerFormService.printerId });
      
      await this.printersModuleService.loadPrinterList();

      this._snackBar.open(this.translateService.instant('PRINTERS.DETAIL.SAVE_EDIT_OK'),
        undefined, 
        { 
          duration: 3000, 
          horizontalPosition: 'start', 
          verticalPosition: "bottom", 
        }
      );
    } catch (error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.SAVE_EDIT_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async deletePrinter() {
    const proceed = await this.confirmDialogService.openDialog({
      data: { 
        messageLabel: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_REMOVE')
      }
    });
    if (!proceed) return;
    
    this.loaderService.show();
    try {
      const printerId = this.printersModuleService.selection()?.printer?.id;

      if(printerId) {
        await this.entityManagerService.printers.deleteOneOnline(printerId, true);
        await this.printersModuleService.loadPrinterList();

        if(!this.isMobile) {
          this.state.go('app.new.settings.printers.general', { checkPendingData: false });
        } else {
          this.printersModuleService.updateSelection(undefined);
          this.printersModuleService.updateShowDetailMobile(false);
          this.state.go('app.new.settings.printers', { checkPendingData: false });
        }

        this._snackBar.open(this.translateService.instant('PRINTERS.DETAIL.DELETE_OK'),
          undefined, 
          { 
            duration: 3000, 
            horizontalPosition: 'start', 
            verticalPosition: "bottom", 
          }
        );
      }
    } catch (error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.DELETE_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  async configure() {
    const proceed = await this.confirmDialogService.openDialog({
      data: { 
        messageLabel: this.translateService.instant('PRINTERS.DETAIL.WANT_TO_CONFIG')
      }
    });
    if (!proceed) return;
      
    this.loaderService.show();
    try {
      const printerToSave = this.printerFormService.preparePrinterForSave();
      const options = this.printersModuleService.selection()?.options;

      await this.fiscalPrintersService.configurePrinter(printerToSave as Printers, options);
      // this.state.reload('app.new.settings.printers.detail', { id: this.printerFormService.printerId });

      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translateService.instant('PRINTERS.DETAIL.CONFIGURATION_COMPLETED')
        }
      });
    } catch (error) {
      const printerId = this.printersModuleService.selection()?.printer?.id;
      this.printerErrorFiscal.show(error, { printerId: printerId });
    } finally {
      this.loaderService.hide();
    }
  }

  async goBack() {
    const confirmed = await this.printerNavigationStateService.checkPendingData();
    if (!confirmed) return;

    this.printersModuleService.updateSelection(undefined);
    this.printersModuleService.updateShowDetailMobile(false);
    this.state.go('app.new.settings.printers', { checkPendingData: false });
  }
}
